<template>
  <div ref="container" class="plv-ia-app plv-ia-notice">
    <div class="plv-ia-notice__inner">
      <create
        v-if="showAddPanel"
        ref="createNotice"
        :has-content="!!noticeContent"
        :is-simple-mode="isSimpleMode"
        @tap-add-link="handleAddLink"
        @tap-submit="handleSubmitNotice"
        @tap-cancel="handleCancelAddPanel"
      />
      <div v-else class="plv-ia-notice__fulled">
        <div class="plv-ia-notice__fulled__content">
          <div class="plv-ia-notice-content" v-html="htmlNotice"></div>
        </div>
        <div class="plv-ia-notice__fulled__footer">
          <button class="plv-ia-btn-new plv-ia-btn-blue" @click="tapAddNewNotice">{{ t('plvIA.notice.publishNew') }}</button>
          <button class="plv-ia-btn-new plv-ia-btn-orange-2" @click="tapDeleteNotice">{{ t('plvIA.common.delete') }}</button>
        </div>
      </div>
    </div>
    <url
      v-if="showAddUrl"
      :link="link"
      @tap-add="handleAddUrl"
      @tap-cancel="handleCancelUrl"
    />
    <popup-confirm
      ref="delConfirm"
      :tips="t('plvIA.notice.confirmDelTips')"
      :ensure-txt="t('plvIA.common.delete')"
      :cancel-txt="t('plvIA.common.cancel')"
      @ensure="handleDelEnsure"
      @cancel="handleDelCancel"
    />
  </div>
</template>

<script>
import Locale from '@/mixins/locale';
import commonMixin from '../app-mixin';
import Url from './Url';
import Create from './Create';
import PopupConfirm from '@/components/popup/Confirm';
import { toSpecialHtml, paste } from '../../assets/utils';

export default {
  name: 'ILSNotice',

  components: {
    Url,
    Create,
    PopupConfirm,
  },

  mixins: [commonMixin, Locale],

  props: {
    /** 公告 SDK 实例 */
    core: {
      type: Object,
      default: null,
    },
    /**
     * 是否为简单模式。
     * 默认情况下，公告组件包含功能：编辑发布公告、移除公告、显示房间内最新公告。
     * 为简单模式时，只包含编辑发布公告功能。其它功能需要自行调用接口进行处理。
     */
    isSimpleMode: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      // 已发布的公告内容
      noticeContent: '',
      showAddPanel: true,
      showAddUrl: false,
      link: {},
    };
  },
  computed: {
    htmlNotice() {
      return toSpecialHtml(this.noticeContent);
    },
  },
  mounted() {
    document.body.addEventListener('click', this.handleClickBody, true);
  },
  beforeDestroy() {
    document.body.removeEventListener('click', this.handleClickBody, true);

    this.removeCoreListeners();
  },
  methods: {
    async afterCoreInited() {
      this.initCoreListeners();
      // 获取聊天室公告内容
      await this.getCurrentNotice();
    },

    handleClickBody(e) {
      if (e.target.className.indexOf('plv-ia-clipboard-btn') === -1) return;
      e.stopPropagation();
      const attrs = e.target.attributes;
      const txt = attrs.getNamedItem('data-clipboard-text').value;
      paste(txt, () => {
        this.$iaMessage.default(this.t('plvIA.common.copySuccessfullyTips'));
      });
    },

    initCoreListeners() {
      this.core
        .on('notice', this.handleNoticeMsg)
        .on('remove-notice', this.handleRemoveNoticeMsg);
    },

    removeCoreListeners() {
      if (!this.core) return;
      this.core
        .off('notice', this.handleNoticeMsg)
        .off('remove-notice', this.handleRemoveNoticeMsg);
    },

    async getCurrentNotice() {
      if (this.isSimpleMode) return;
      try {
        const noticeContent = await this.core.getCurrentNotice();
        this.noticeContent = noticeContent;
        this.showAddPanel = !noticeContent;
      } catch (error) {
        this.$iaMessage.default('公告内容获取失败。' + error.message);
      }
    },

    handleNoticeMsg({ content }) {
      if (this.isSimpleMode) {
        this.$emit('close', {
          // 新公告已发布
          type: 'published',
        });
        return;
      }
      if (this.isSubmiting) {
        this.isSubmiting = false;
        this.$iaMessage.default(this.t('plvIA.notice.publishSuccessfullyTips'));
      }
      this.noticeContent = content;
      this.showAddPanel = false;
    },

    handleRemoveNoticeMsg() {
      if (this.isDeleting) {
        this.isDeleting = false;
        this.$iaMessage.default(this.t('plvIA.common.deleteSuccessfullyTips'));
        this.$refs.delConfirm.hide();
      }
      this.noticeContent = '';
      this.showAddPanel = true;
    },

    handleAddLink(link) {
      this.link = link;
      this.showAddUrl = true;
    },
    handleAddUrl(data) {
      this.link = data;
      this.$refs.createNotice.addLink(data);
      this.showAddUrl = false;
    },
    handleCancelUrl() {
      this.showAddUrl = false;
    },
    // 取消发布公告
    handleCancelAddPanel() {
      if (this.isSimpleMode) {
        this.$emit('close', {
          // 取消发布公告
          type: 'cancelPublish',
        });
        return;
      }
      this.showAddPanel = false;
    },
    // 发布公告
    async handleSubmitNotice(content) {
      if (!content) return;
      this.isSubmiting = true;

      try {
        await this.core.publishNotice(content);
      } catch (error) {
        this.$iaMessage.default('发布失败！' + error.message);
      }
    },
    // 发布新公告
    tapAddNewNotice() {
      this.showAddPanel = true;
    },
    // 删除公告
    tapDeleteNotice() {
      this.$refs.delConfirm.show();
    },
    handleDelEnsure() {
      this.isDeleting = true;
      try {
        this.core.unpublishNotice();
        this.$iaMessage.default('删除成功！');
      } catch (error) {
        this.$iaMessage.default('删除失败！' + error.message);
      }
    },
    handleDelCancel() {
      this.$refs.delConfirm.hide();
    }
  }
};
</script>

<style lang="scss">
@import '../../assets/styles/main.scss';

.plv-ia-notice {
  width: 100%;
  height: 100%;
  &__inner {
    width: 100%;
    height: 100%;
    position: relative;
  }
}

.plv-ia-notice__fulled {
  height: 100%;
  width: 100%;
  flex-direction: column;
  align-items: center;
  padding: 24px 0;
  box-sizing: border-box;
  .plv-ia-btn-2 {
    margin-left: 20px;
  }
}

.plv-ia-notice__fulled__content {
  width: 100%;
  height: 100%;
  padding-bottom: 47px;
  color: #333;
  font-size: 14px;
  line-height: 24px;
  box-sizing: border-box;
}

.plv-ia-notice-content {
  height: 100%;
  overflow: auto;
  padding: 0 24px;
  word-break: break-all;
}

.plv-ia-notice__fulled__footer {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  text-align: center;
  height: 56px;
  button + button {
    margin-left: 16px;
  }
}

.plv-ia-clipboard-btn {
  border: none;
  font-size: 14px;
  color: #2196f3;
  outline: none;
  background: transparent;
  cursor: pointer;
}
</style>
